/**
 * Track Spline & Geometry Engine for Jolly Tesla Telemetry.
 * Implements Catmull-Rom closed splines, arc-length parameterization,
 * ribbon normal offsets, and closest-point snapping for WGS84 GPS tracks.
 */

export interface TrackPoint {
  lat: number;
  lng: number;
  widthM?: number;
}

export type TrackAnnotationType = 'corner' | 'sector' | 'finish_line' | 'speed_trap' | 'custom';

export interface TrackAnnotation {
  id?: string;
  trackId?: string;
  type: TrackAnnotationType;
  name: string;
  orderIndex: number;
  location?: TrackPoint; // For finish_line, sector, speed_trap, custom
  entry?: TrackPoint;    // For corners: Entry / Turn-in / Braking point
  apex?: TrackPoint;     // For corners: Apex / Clipping point
  exit?: TrackPoint;     // For corners: Exit / Track-out / Full Throttle point
  direction?: 'left' | 'right';
  metadata?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

export interface TrackSegment {
  index: number;
  lat: number;
  lng: number;
  distanceM?: number;
}

export interface TrackShape {
  id?: string;
  trackId: string;
  centerline: TrackPoint[];
  trackWidthM: number;
  innerBoundary: TrackPoint[];
  outerBoundary: TrackPoint[];
  totalLengthM: number;
  segments?: TrackSegment[];
  createdAt?: string;
  updatedAt?: string;
}

export interface SplineSample {
  point: TrackPoint;
  t: number; // 0.0 to 1.0
  distanceM: number;
  normal: { x: number; y: number }; // local unit normal vector in meters (dx, dy)
  curvature: number; // signed curvature (>0 left, <0 right)
  widthM: number; // local track width at this point in meters
}

const EARTH_RADIUS_M = 6371000.0;

/** Distance in meters between two GPS coordinates using Haversine formula */
export function haversineDistanceM(p1: TrackPoint, p2: TrackPoint): number {
  const lat1 = (p1.lat * Math.PI) / 180;
  const lat2 = (p2.lat * Math.PI) / 180;
  const dLat = ((p2.lat - p1.lat) * Math.PI) / 180;
  const dLon = ((p2.lng - p1.lng) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_M * c;
}

/** Evaluates a single Catmull-Rom segment between p1 and p2 with neighbors p0 and p3 at local t in [0, 1] */
function catmullRomPoint(p0: TrackPoint, p1: TrackPoint, p2: TrackPoint, p3: TrackPoint, t: number): TrackPoint {
  const t2 = t * t;
  const t3 = t2 * t;

  const f0 = -0.5 * t3 + 1.0 * t2 - 0.5 * t;
  const f1 = 1.5 * t3 - 2.5 * t2 + 1.0;
  const f2 = -1.5 * t3 + 2.0 * t2 + 0.5 * t;
  const f3 = 0.5 * t3 - 0.5 * t2;

  return {
    lat: f0 * p0.lat + f1 * p1.lat + f2 * p2.lat + f3 * p3.lat,
    lng: f0 * p0.lng + f1 * p1.lng + f2 * p2.lng + f3 * p3.lng,
  };
}

/** Evaluates first derivative (tangent vector) of Catmull-Rom segment at local t */
function catmullRomDerivative(p0: TrackPoint, p1: TrackPoint, p2: TrackPoint, p3: TrackPoint, t: number): { dLat: number; dLng: number } {
  const t2 = t * t;

  const df0 = -1.5 * t2 + 2.0 * t - 0.5;
  const df1 = 4.5 * t2 - 5.0 * t;
  const df2 = -4.5 * t2 + 4.0 * t + 0.5;
  const df3 = 1.5 * t2 - 1.0 * t;

  return {
    dLat: df0 * p0.lat + df1 * p1.lat + df2 * p2.lat + df3 * p3.lat,
    dLng: df0 * p0.lng + df1 * p1.lng + df2 * p2.lng + df3 * p3.lng,
  };
}

/** Evaluates second derivative of Catmull-Rom segment at local t */
function catmullRomSecondDerivative(p0: TrackPoint, p1: TrackPoint, p2: TrackPoint, p3: TrackPoint, t: number): { d2Lat: number; d2Lng: number } {
  const d2f0 = -3.0 * t + 2.0;
  const d2f1 = 9.0 * t - 5.0;
  const d2f2 = -9.0 * t + 4.0;
  const d2f3 = 3.0 * t - 1.0;

  return {
    d2Lat: d2f0 * p0.lat + d2f1 * p1.lat + d2f2 * p2.lat + d2f3 * p3.lat,
    d2Lng: d2f0 * p0.lng + d2f1 * p1.lng + d2f2 * p2.lng + d2f3 * p3.lng,
  };
}

/**
 * Computes high-resolution equidistant samples along a closed Catmull-Rom spline formed by control points,
 * with continuous smooth width transitions between waypoints.
 */
export function sampleClosedSpline(
  controlPoints: TrackPoint[], 
  defaultTrackWidthM: number = 8.0, 
  samplesPerSegment: number = 30
): SplineSample[] {
  const n = controlPoints.length;
  if (n < 3) return [];

  const rawSamples: { 
    point: TrackPoint; 
    tParam: number; 
    dLat: number; 
    dLng: number; 
    d2Lat: number; 
    d2Lng: number; 
    widthM: number;
  }[] = [];

  for (let i = 0; i < n; i++) {
    const p0 = controlPoints[(i - 1 + n) % n];
    const p1 = controlPoints[i];
    const p2 = controlPoints[(i + 1) % n];
    const p3 = controlPoints[(i + 2) % n];

    const w1 = (p1.widthM !== undefined && p1.widthM > 0) ? p1.widthM : defaultTrackWidthM;
    const w2 = (p2.widthM !== undefined && p2.widthM > 0) ? p2.widthM : defaultTrackWidthM;

    for (let s = 0; s < samplesPerSegment; s++) {
      const localT = s / samplesPerSegment;
      const globalT = (i + localT) / n;
      const pt = catmullRomPoint(p0, p1, p2, p3, localT);
      const deriv = catmullRomDerivative(p0, p1, p2, p3, localT);
      const d2 = catmullRomSecondDerivative(p0, p1, p2, p3, localT);

      // Smooth cubic Hermite interpolation of track width across the segment
      const smoothTau = localT * localT * (3.0 - 2.0 * localT);
      const localWidthM = w1 + (w2 - w1) * smoothTau;

      rawSamples.push({
        point: pt,
        tParam: globalT,
        dLat: deriv.dLat,
        dLng: deriv.dLng,
        d2Lat: d2.d2Lat,
        d2Lng: d2.d2Lng,
        widthM: localWidthM
      });
    }
  }

  // Compute cumulative distances and unit normals
  const totalCount = rawSamples.length;
  const result: SplineSample[] = [];
  let cumDistance = 0;

  for (let i = 0; i < totalCount; i++) {
    const curr = rawSamples[i];
    if (i > 0) {
      cumDistance += haversineDistanceM(rawSamples[i - 1].point, curr.point);
    }

    const latRad = (curr.point.lat * Math.PI) / 180;
    const cosLat = Math.cos(latRad);

    // Tangent vector in meters
    const tx = (curr.dLng * Math.PI / 180) * EARTH_RADIUS_M * cosLat;
    const ty = (curr.dLat * Math.PI / 180) * EARTH_RADIUS_M;
    const lenT = Math.sqrt(tx * tx + ty * ty) || 1;

    // Normal vector perpendicular to tangent (rotate 90 deg counter-clockwise in cartesian space)
    const nx = -ty / lenT;
    const ny = tx / lenT;

    // 2D Curvature kappa = (x' y'' - y' x'') / (x'^2 + y'^2)^(3/2)
    const d2x = (curr.d2Lng * Math.PI / 180) * EARTH_RADIUS_M * cosLat;
    const d2y = (curr.d2Lat * Math.PI / 180) * EARTH_RADIUS_M;
    const curvature = (tx * d2y - ty * d2x) / Math.pow(lenT, 3);

    result.push({
      point: curr.point,
      t: curr.tParam,
      distanceM: cumDistance,
      normal: { x: nx, y: ny },
      curvature,
      widthM: curr.widthM,
    });
  }

  return result;
}

/**
 * Computes parallel inner and outer boundary curves with per-segment/per-point variable normal offsets.
 */
export function computeRibbonBoundaries(
  controlPoints: TrackPoint[],
  trackWidthM: number = 8.0,
  samplesPerSegment: number = 20
): { inner: TrackPoint[]; outer: TrackPoint[]; totalLengthM: number } {
  const samples = sampleClosedSpline(controlPoints, trackWidthM, samplesPerSegment);
  if (samples.length === 0) {
    return { inner: [], outer: [], totalLengthM: 0 };
  }

  const inner: TrackPoint[] = [];
  const outer: TrackPoint[] = [];

  for (const s of samples) {
    const latRad = (s.point.lat * Math.PI) / 180;
    const cosLat = Math.cos(latRad);
    const halfWidth = (s.widthM || trackWidthM || 8.0) / 2.0;

    // Convert meter offset along normal back to lat/lon degrees
    const dLat = ((s.normal.y * halfWidth) / EARTH_RADIUS_M) * (180 / Math.PI);
    const dLon = ((s.normal.x * halfWidth) / (EARTH_RADIUS_M * cosLat)) * (180 / Math.PI);

    inner.push({ lat: s.point.lat + dLat, lng: s.point.lng + dLon });
    outer.push({ lat: s.point.lat - dLat, lng: s.point.lng - dLon });
  }

  const totalLengthM = samples.length > 0
    ? samples[samples.length - 1].distanceM + haversineDistanceM(samples[samples.length - 1].point, samples[0].point)
    : 0;

  return { inner, outer, totalLengthM };
}

/**
 * Finds the closest parametric position t in [0, 1) on the spline for any GPS point
 * with sub-sample continuous orthogonal projection.
 */
export function findClosestParametricT(
  target: TrackPoint,
  samples: SplineSample[]
): { t: number; point: TrackPoint; distanceToCurveM: number; index: number; normal: { x: number; y: number } } {
  const n = samples.length;
  if (n === 0) {
    return { t: 0, point: target, distanceToCurveM: 0, index: 0, normal: { x: 0, y: 1 } };
  }

  // 1. Find closest discrete sample candidate
  let bestIdx = 0;
  let bestDiscreteDist = Infinity;

  for (let i = 0; i < n; i++) {
    const d = haversineDistanceM(target, samples[i].point);
    if (d < bestDiscreteDist) {
      bestDiscreteDist = d;
      bestIdx = i;
    }
  }

  if (n < 2) {
    return {
      t: samples[0].t,
      point: samples[0].point,
      distanceToCurveM: bestDiscreteDist,
      index: 0,
      normal: samples[0].normal || { x: 0, y: 1 },
    };
  }

  // 2. Project target point onto adjacent segments to find continuous sub-sample position
  const targetLatRad = (target.lat * Math.PI) / 180;
  const cosLat = Math.cos(targetLatRad);

  const toMeters = (pt: TrackPoint) => ({
    x: ((pt.lng - target.lng) * (Math.PI / 180)) * EARTH_RADIUS_M * cosLat,
    y: ((pt.lat - target.lat) * (Math.PI / 180)) * EARTH_RADIUS_M,
  });

  const testSegments = [
    { i0: (bestIdx - 1 + n) % n, i1: bestIdx },
    { i0: bestIdx, i1: (bestIdx + 1) % n },
  ];

  let bestContinuousDist = Infinity;
  let bestT = samples[bestIdx].t;
  let bestPoint = samples[bestIdx].point;
  let bestNormal = samples[bestIdx].normal || { x: 0, y: 1 };
  let bestSegmentIndex = bestIdx;

  for (const seg of testSegments) {
    const s0 = samples[seg.i0];
    const s1 = samples[seg.i1];

    const p0 = toMeters(s0.point);
    const p1 = toMeters(s1.point);

    const vx = p1.x - p0.x;
    const vy = p1.y - p0.y;
    const lenSq = vx * vx + vy * vy;

    let u = 0;
    if (lenSq > 1e-12) {
      const wx = -p0.x;
      const wy = -p0.y;
      const dot = wx * vx + wy * vy;
      u = Math.max(0, Math.min(1, dot / lenSq));
    }

    const qx = p0.x + u * vx;
    const qy = p0.y + u * vy;
    const distM = Math.sqrt(qx * qx + qy * qy);

    if (distM < bestContinuousDist) {
      bestContinuousDist = distM;
      bestSegmentIndex = u < 0.5 ? seg.i0 : seg.i1;

      let t0 = s0.t;
      let t1 = s1.t;
      if (t1 < t0) {
        t1 += 1.0;
      }
      bestT = ((t0 + u * (t1 - t0)) % 1.0 + 1.0) % 1.0;

      bestPoint = {
        lat: s0.point.lat + u * (s1.point.lat - s0.point.lat),
        lng: s0.point.lng + u * (s1.point.lng - s0.point.lng),
      };

      const n0 = s0.normal || { x: 0, y: 1 };
      const n1 = s1.normal || { x: 0, y: 1 };
      const nx = n0.x + u * (n1.x - n0.x);
      const ny = n0.y + u * (n1.y - n0.y);
      const nLen = Math.sqrt(nx * nx + ny * ny) || 1;
      bestNormal = { x: nx / nLen, y: ny / nLen };
    }
  }

  return {
    t: bestT,
    point: bestPoint,
    distanceToCurveM: bestContinuousDist,
    index: bestSegmentIndex,
    normal: bestNormal,
  };
}

/**
 * Evaluates the GPS point on the spline at a specific parametric position t in [0, 1).
 */
export function getPointAtT(t: number, controlPoints: TrackPoint[]): TrackPoint {
  const n = controlPoints.length;
  if (n < 3) return controlPoints[0] || { lat: 0, lng: 0 };

  const normT = ((t % 1) + 1) % 1;
  const scaledT = normT * n;
  const segIdx = Math.floor(scaledT) % n;
  const localT = scaledT - Math.floor(scaledT);

  const p0 = controlPoints[(segIdx - 1 + n) % n];
  const p1 = controlPoints[segIdx];
  const p2 = controlPoints[(segIdx + 1) % n];
  const p3 = controlPoints[(segIdx + 2) % n];

  return catmullRomPoint(p0, p1, p2, p3, localT);
}

/**
 * Bakes the centerline spline into numSegments (default: 200) equidistant static GPS segments.
 * Segment 0 starts at the Start / Finish line annotation (or first control point if not specified)
 * and progresses along the track in driving direction.
 */
export function bakeTrackSegments(
  controlPoints: TrackPoint[],
  numSegments: number = 200,
  finishLineLocation?: TrackPoint
): TrackSegment[] {
  const n = controlPoints.length;
  if (n < 3 || numSegments < 2) return [];

  // High-density sampling to obtain accurate arc-length parameterization
  const samples = sampleClosedSpline(controlPoints, 8.0, 50);
  if (samples.length === 0) return [];

  const totalSamples = samples.length;
  const loopClosingDist = haversineDistanceM(samples[totalSamples - 1].point, samples[0].point);
  const totalLengthM = samples[totalSamples - 1].distanceM + loopClosingDist;
  if (totalLengthM <= 0) return [];

  // Cumulative distances array covering the wrap-around
  const cumDist: number[] = new Array(totalSamples + 1);
  cumDist[0] = 0;
  for (let i = 0; i < totalSamples; i++) {
    cumDist[i] = samples[i].distanceM;
  }
  cumDist[totalSamples] = totalLengthM;

  // Determine starting distance corresponding to the Start / Finish line
  let startDist = 0.0;
  if (finishLineLocation) {
    const closest = findClosestParametricT(finishLineLocation, samples);
    const closestIdx = Math.max(0, Math.min(totalSamples - 1, closest.index));
    startDist = samples[closestIdx].distanceM;
  }

  const segments: TrackSegment[] = [];
  const segStepM = totalLengthM / numSegments;

  for (let k = 0; k < numSegments; k++) {
    const targetDist = (startDist + k * segStepM) % totalLengthM;

    // Binary search for interval in cumDist
    let low = 0;
    let high = totalSamples;
    while (low <= high) {
      const mid = (low + high) >> 1;
      if (cumDist[mid] <= targetDist) {
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    const j = Math.max(0, Math.min(totalSamples - 1, high));
    const d0 = cumDist[j];
    const d1 = cumDist[j + 1];
    const span = d1 - d0;
    const u = span > 1e-9 ? (targetDist - d0) / span : 0;

    const p0 = samples[j].point;
    const p1 = samples[(j + 1) % totalSamples].point;

    const lat = p0.lat + u * (p1.lat - p0.lat);
    const lng = p0.lng + u * (p1.lng - p0.lng);

    segments.push({
      index: k,
      lat: Math.round(lat * 1_000_000) / 1_000_000,
      lng: Math.round(lng * 1_000_000) / 1_000_000,
      distanceM: Math.round(k * segStepM * 10) / 10,
    });
  }

  return segments;
}

/**
 * Finds the index of the closest baked segment for a given GPS coordinate.
 */
export function findClosestSegmentIndex(
  target: TrackPoint,
  segments: TrackSegment[]
): number {
  if (segments.length === 0) return -1;
  let bestIdx = 0;
  let bestDist = Infinity;

  for (let i = 0; i < segments.length; i++) {
    const d = haversineDistanceM(target, segments[i]);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }

  return bestIdx;
}

/**
 * Returns true if the corner name is empty or a standard auto-generated turn label
 * (e.g. "", "Turn", "Turn 3", "T3", "Kurve 2") rather than a custom corner name.
 */
export function isGenericCornerName(name?: string | null): boolean {
  const trimmed = (name || '').trim();
  if (!trimmed) return true;
  return /^(turn|t|kurve)(\s*\d+)?$/i.test(trimmed);
}

/**
 * Formats a corner label dynamically using its 1-based corner number along the track.
 * Standard/empty names render as "T1" (or "Turn 1" when prefix="Turn"), while custom
 * names render as "T1 · Hairpin" (or "Turn 1 · Hairpin").
 */
export function formatDynamicCornerLabel(
  name: string | undefined | null,
  cornerNum: number,
  prefix: string = 'T'
): string {
  const num = cornerNum > 0 ? cornerNum : 1;
  const base = prefix === 'T' ? `T${num}` : `${prefix} ${num}`;
  if (isGenericCornerName(name)) {
    return base;
  }
  return `${base} · ${name!.trim()}`;
}

/**
 * Dynamically orders and renumbers all corner (and sector) annotations sequentially (1..N)
 * by their distance along the driving direction from the Start / Finish line.
 */
export function renumberCornersByFinishLine(
  annotations: TrackAnnotation[],
  splineSamples?: SplineSample[]
): TrackAnnotation[] {
  const finishLine = annotations.find(a => a.type === 'finish_line');
  let finishT = 0.0;
  if (finishLine?.location && splineSamples && splineSamples.length > 2) {
    finishT = findClosestParametricT(finishLine.location, splineSamples).t;
  }

  const finishLines: TrackAnnotation[] = [];
  const sectors: TrackAnnotation[] = [];
  const corners: TrackAnnotation[] = [];
  const others: TrackAnnotation[] = [];

  for (const a of annotations) {
    if (a.type === 'finish_line') {
      finishLines.push({ ...a, orderIndex: 0 });
    } else if (a.type === 'sector') {
      sectors.push(a);
    } else if (a.type === 'corner') {
      corners.push(a);
    } else {
      others.push(a);
    }
  }

  const getRelT = (pt?: TrackPoint): number | null => {
    if (!pt || !splineSamples || splineSamples.length <= 2) return null;
    const t = findClosestParametricT(pt, splineSamples).t;
    return ((t - finishT) % 1.0 + 1.0) % 1.0;
  };

  sectors.sort((s1, s2) => {
    const r1 = getRelT(s1.location);
    const r2 = getRelT(s2.location);
    if (r1 !== null && r2 !== null && Math.abs(r1 - r2) > 1e-9) {
      return r1 - r2;
    }
    return (s1.orderIndex || 0) - (s2.orderIndex || 0);
  });

  const renumberedSectors = sectors.map((s, idx) => ({
    ...s,
    orderIndex: idx + 1,
  }));

  corners.sort((c1, c2) => {
    const r1 = getRelT(c1.apex || c1.location || c1.entry);
    const r2 = getRelT(c2.apex || c2.location || c2.entry);
    if (r1 !== null && r2 !== null && Math.abs(r1 - r2) > 1e-9) {
      return r1 - r2;
    }
    return (c1.orderIndex || 0) - (c2.orderIndex || 0);
  });

  const renumberedCorners = corners.map((c, idx) => {
    const num = idx + 1;
    const newName = isGenericCornerName(c.name) ? `Turn ${num}` : c.name.trim();
    return {
      ...c,
      name: newName,
      orderIndex: num,
    };
  });

  return [...finishLines, ...renumberedSectors, ...renumberedCorners, ...others];
}

